import { useNavigate, useParams } from "react-router-dom";
import { Icon, Typography } from "@/components/atoms";
import { BulkImportReport } from "@/components/organisms/BulkBooking/BulkImportReport";
import { REPORT_MAX_WIDTH } from "@/components/organisms/BulkBooking/reportLayout";
import { clsx } from "@/utils";

/**
 * A past bulk import, at a URL that keeps working (DEV-1215).
 *
 * Where the completion email lands. The run outlives the tab that started it, so by the time anyone
 * reads that email the wizard is long gone — without somewhere to send them, a report saying "3
 * bookings failed" would be a dead end. In-app nobody needs this URL: the progress Snackbar opens the
 * same report in a modal, over whatever the reader was doing.
 *
 * **Portal-neutral, deliberately.** A bulk import can be started by a client, an assistant or an
 * operator, and those three live behind different route trees and different account guards. One
 * email cannot carry three links, so the page sits outside all of them behind plain authentication,
 * and the server decides whether the reader is allowed to see this particular import.
 *
 * The page owns almost nothing. Everything from the title down is `BulkImportReport`, which the modal
 * renders too — the only thing added here is the Back control, and the card it all sits in.
 */
export const BulkImportPage = () => {
  const { bulkId } = useParams();
  const navigate = useNavigate();

  return (
    // Capped and centred like My Clients, so the report doesn't stretch across an ultrawide screen.
    // The cap is shared with the modal — see reportLayout.
    <div className={clsx("mx-auto flex w-full flex-col px-5 py-6 md:px-8", REPORT_MAX_WIDTH)}>
      {/* p-6 to match the Modal atom's own panel padding, so the two frames sit identically. */}
      <div className="rounded-lg border border-neutral-mid-gray bg-white p-6">
        {bulkId && (
          <BulkImportReport
            bulkId={bulkId}
            headerAction={
              // Home, not a named destination: this page is reached from an email, so there is no
              // "back" to speak of, and where home IS depends on which portal the reader belongs to.
              <button type="button" onClick={() => navigate("/")} className="flex shrink-0 items-center gap-1.5 text-primary">
                <Icon name="ArrowLeft" size="sm" />
                <Typography variant="action">Back</Typography>
              </button>
            }
          />
        )}
      </div>
    </div>
  );
};
